import { ParsedUrlQuery } from 'querystring';
import { getCurrentSkuType, CurrentSku, Data, Product, Offer, Sku } from './types';

export const getCurrentSku: getCurrentSkuType = (data: Data, queryString: ParsedUrlQuery): CurrentSku | null => {
  if (!data?.product) {
    return null;
  }

  const sku = getSku(data.product, queryString);
  const qsDiffs = sku?.diffs?.reduce((acc: ParsedUrlQuery, curr) => {
    acc[curr.type.toLowerCase()] = curr.value;
    return acc;
  }, {});
  const qs = qsDiffs ? { ...qsDiffs, condition: queryString?.condition || sku?.offers?.result?.[0]?.condition } : {};

  const mainOffer = data.product?.mainOffer;
  const isMainOfferFromSku = sku?.id === mainOffer?.sku;

  if (mainOffer && isMainOfferFromSku) {
    const buyboxToken = (queryString?.buyboxToken ?? '') as string;
    const offers = { meta: { ...sku?.offers?.meta, buyboxToken }, result: [mainOffer] };
    return { ...sku, qs, offers };
  }

  return { ...sku, qs };
};

const getSku = (product: Product, qs: ParsedUrlQuery) => {
  const { diffs, skus, mainOffer } = product;

  const hasDiffsSku = diffs?.some(diff => qs?.[diff.type.toLowerCase()]);
  const skuFromQs = skus?.find(({ diffs }) => diffs?.every(diff => diff.value === qs?.[diff.type.toLowerCase()]));

  if (!skuFromQs && hasDiffsSku) {
    return null;
  }

  const hasMainOffer = mainOffer && mainOffer?.id === qs?.offerId;
  const skuPreSelected = hasMainOffer && !hasDiffsSku ? skus.find(sku => sku.id === mainOffer?.sku) : skuFromQs;
  const skuWithCondition = getSkuWithCondition(skus, skuPreSelected, qs?.condition as string | undefined);

  return skuWithCondition;
};

export const getSkuWithCondition = (skus: Sku[], skuPreSelected?: Sku, condition?: string) => {
  if (!condition && skuPreSelected) {
    return skuPreSelected;
  }

  if (!condition) {
    const offers = skus?.reduce(
      (skuOffer: Offer[], { offers }) => offers && (skuOffer ?? []).concat(offers.result),
      []
    );
    const skuFirstOffer = skus?.find(({ id }) => id === offers?.[0]?.sku);

    return skuFirstOffer || skus?.[0];
  }

  if (skuPreSelected) {
    const hasSkuWithCondition = skuPreSelected?.offers?.result?.some(offer => offer?.condition === condition);
    return hasSkuWithCondition ? skuPreSelected : null;
  }

  return skus?.find(({ offers }) => offers?.result?.find(offer => offer?.condition === condition));
};
